import { useEffect, useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, ChevronLeft, ChevronRight, Pause, Play, Maximize, Minimize } from 'lucide-react';
import { SlideRenderer, RealProduct } from './SlideRenderer';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/services/api';

interface SlideDef {
  type: string;
  content: any;
  duration_ms?: number;
  name?: string;
}

interface Props {
  slides: SlideDef[];
  title: string;
  tenantBranding?: { logo_url?: string; primary_color?: string };
  orientation?: 'landscape' | 'portrait';
  onClose: () => void;
  onUseTemplate?: () => void;
  useButtonLabel?: string;
}

const STAGE_PADDING = 48; // p-6 (24px) em cada lado

export function PlaylistPreviewModal({ slides, title, tenantBranding, orientation = 'landscape', onClose, onUseTemplate, useButtonLabel }: Props) {
  // Canvas lógico segue a orientação configurada na playlist — 1920×1080 deitada,
  // 1080×1920 em pé. O scale calcula encaixe proporcional no stage disponível.
  const isPortrait = orientation === 'portrait';
  const TV_WIDTH = isPortrait ? 1080 : 1920;
  const TV_HEIGHT = isPortrait ? 1920 : 1080;
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [fs, setFs] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  // Mede o quadro disponível e calcula um scale que mantenha 1920×1080 caibam mantendo 16:9.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    let raf = 0;
    const recalc = () => {
      const rect = el.getBoundingClientRect();
      const availW = Math.max(0, rect.width - STAGE_PADDING);
      const availH = Math.max(0, rect.height - STAGE_PADDING);
      const s = Math.min(availW / TV_WIDTH, availH / TV_HEIGHT);
      setScale(s > 0 ? s : 1);
    };
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(recalc);
    });
    ro.observe(el);
    recalc();
    return () => {
      ro.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [TV_WIDTH, TV_HEIGHT]);

  // Busca produtos reais do tenant uma única vez (compartilhado entre todos os slides)
  const { data: productsData } = useQuery({
    queryKey: ['preview-products'],
    queryFn: () => apiClient.getProducts({ limit: 200 }).then(r => r.data),
    staleTime: 60_000,
  });
  const realProducts: RealProduct[] = productsData?.items ?? [];

  // ESC fecha
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === ' ') { e.preventDefault(); setPlaying(p => !p); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Auto-advance
  useEffect(() => {
    if (!playing || slides.length === 0) return;
    const dur = slides[idx]?.duration_ms ?? 5000;
    const t = setTimeout(() => setIdx(i => (i + 1) % slides.length), dur);
    return () => clearTimeout(t);
  }, [idx, playing, slides]);

  function prev() { setIdx(i => (i - 1 + slides.length) % slides.length); }
  function next() { setIdx(i => (i + 1) % slides.length); }

  function toggleFullscreen() {
    if (!fs) {
      containerRef.current?.requestFullscreen?.();
      setFs(true);
    } else {
      document.exitFullscreen?.();
      setFs(false);
    }
  }

  if (slides.length === 0) {
    return (
      <div className="fixed inset-0 z-50 grid place-items-center bg-black/90">
        <div className="bg-card p-8 rounded-lg max-w-sm text-center">
          <h3 className="text-lg font-bold">Sem slides</h3>
          <p className="text-sm text-muted-foreground mt-2">Esta playlist não tem slides ainda.</p>
          <Button onClick={onClose} className="mt-4">Fechar</Button>
        </div>
      </div>
    );
  }

  const current = slides[idx];
  const progress = ((idx + 1) / slides.length) * 100;

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" ref={containerRef}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 bg-black/60 backdrop-blur border-b border-white/10">
        <div className="flex items-center gap-3 text-white min-w-0">
          <div className="font-bold truncate">{title}</div>
          <span
            className={`shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${
              isPortrait
                ? 'bg-amber-500/20 text-amber-200 border border-amber-400/40'
                : 'bg-sky-500/20 text-sky-200 border border-sky-400/40'
            }`}
            title={isPortrait ? 'TV em pé (vertical)' : 'TV deitada (horizontal)'}
          >
            {isPortrait ? '▮ Vertical' : '▬ Horizontal'}
          </span>
          <div className="text-xs text-white/60">·</div>
          <div className="text-xs text-white/60 truncate">
            Slide {idx + 1}/{slides.length} {current.name && `— ${current.name}`}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onUseTemplate && (
            <Button onClick={onUseTemplate} variant="gradient" size="sm">
              {useButtonLabel ?? 'Usar este modelo'}
            </Button>
          )}
          <button onClick={toggleFullscreen} className="text-white/70 hover:text-white p-2" title="Tela cheia">
            {fs ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
          </button>
          <button onClick={onClose} className="text-white/70 hover:text-white p-2" title="Fechar (ESC)">
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Stage: canvas lógico fixo 1920×1080 escalado proporcionalmente — espelha exatamente a TV */}
      <div ref={stageRef} className="flex-1 grid place-items-center p-6 overflow-hidden">
        <div
          className="bg-black rounded-lg overflow-hidden shadow-2xl ring-1 ring-white/10"
          style={{ width: TV_WIDTH * scale, height: TV_HEIGHT * scale }}
        >
          <div
            style={{
              width: TV_WIDTH,
              height: TV_HEIGHT,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
            }}
          >
            <SlideRenderer
              type={current.type}
              content={typeof current.content === 'string' ? JSON.parse(current.content) : current.content}
              name={current.name}
              tenantBranding={tenantBranding}
              realProducts={realProducts}
              durationMs={current.duration_ms}
            />
          </div>
        </div>
      </div>

      {/* Controles */}
      <div className="px-6 py-4 bg-black/60 backdrop-blur border-t border-white/10">
        {/* Progresso */}
        <div className="h-1 bg-white/10 rounded-full overflow-hidden mb-3">
          <div className="h-full bg-brand-500 transition-all" style={{ width: `${progress}%` }} />
        </div>
        <div className="flex items-center justify-center gap-2">
          <button onClick={prev} className="text-white/70 hover:text-white p-3" title="Anterior (←)">
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button onClick={() => setPlaying(p => !p)} className="text-white p-3 bg-white/10 rounded-full" title="Pausar (Espaço)">
            {playing ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
          </button>
          <button onClick={next} className="text-white/70 hover:text-white p-3" title="Próximo (→)">
            <ChevronRight className="h-6 w-6" />
          </button>
        </div>
        {/* Thumbnails */}
        <div className="flex gap-2 overflow-x-auto mt-3 px-12">
          {slides.map((s, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              className={`shrink-0 px-3 py-1.5 rounded text-xs font-medium transition-all ${
                i === idx ? 'bg-brand-600 text-white' : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
            >
              {i + 1}. {s.name ?? s.type}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
